import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import products from "../../data/products";
import Footer from "../../components/Footer";
import { createUser } from "../../api/userApi";
import { createOrder, verifyPayment } from "../../api/paymentApi";
import { getProductPrices, getPriceForSku } from "../../api/productPriceApi";
import { Info, Star } from "lucide-react";
import SEOHead from "../../seo/SEOHead";
import { getProductSEO } from "../../seo/seoConfig";
import { productSchema, breadcrumbSchema } from "../../seo/structuredData";
import Marquee from "react-fast-marquee";
import certLogoTbs1 from "../../assets/logo4.png";
import certLogoBis from "../../assets/BIS.png";
import certLogoIso from "../../assets/logo2.png";
import certLogoTbs2 from "../../assets/logo3.png";
import "./ProductPage.css";
import "../ShopPage/ShopPage.css";

const customerFeedbackList = [
  {
    id: 1,
    quote: "Stove ka look kaafi premium hai, kitchen mein bahut achha lag raha hai.",
    author: "Rahul Sharma",
    location: "Delhi NCR",
    rating: 5,
    product: "4 Burner Glass Top PNG"
  },
  {
    id: 2,
    quote: "Flame kaafi strong hai aur cooking bhi fast ho rahi hai.",
    author: "Anita Patel",
    location: "Ahmedabad",
    rating: 5,
    product: "3 Burner Stainless Steel PNG"
  },
  {
    id: 3,
    quote: "Glass top clean karna easy hai, overall quality achhi lagi.",
    author: "Priya Singh",
    location: "Mumbai",
    rating: 5,
    product: "2 Burner Glass Top PNG"
  },
  {
    id: 4,
    quote: "4 burner model family ke liye perfect laga, space bhi achha milta hai.",
    author: "Vikram Mehta",
    location: "Pune",
    rating: 5,
    product: "4 Burner Glass Top PNG"
  },
  {
    id: 5,
    quote: "Price ke according finishing aur design kaafi premium hai.",
    author: "Amit Kumar",
    location: "Bengaluru",
    rating: 4,
    product: "3 Burner Glass Top PNG"
  }
];

// Certification data matching existing certifications
const certificationsList = [
  { id: "cert-1", name: "Tanzania Bureau of Standards", shortName: "TBS", logo: certLogoTbs1 },
  { id: "cert-2", name: "Bureau of Indian Standards", shortName: "BIS", logo: certLogoBis },
  { id: "cert-3", name: "ISO 9001:2015", shortName: "ISO", logo: certLogoIso },
  { id: "cert-4", name: "Tanzania Bureau of standards", shortName: "TBS", logo: certLogoTbs2 },
];

// Load all images from src/assets/RF-img
const loadRealFlameImages = () => {
  try {
    const context = require.context("../../assets/RF-img", false, /\.(png|jpe?g|svg|webp|avif)$/i);
    const keys = context.keys().sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    return keys.map((key) => context(key));
  } catch (e) {
    console.warn("Could not load RealFlame asset images", e);
    return [];
  }
};
const realFlameImages = loadRealFlameImages();

/* ─── Shared Warranty & Installation section ─────────────────────────────── */
const WarrantySection = () => (
  <div className="warranty-section">
    <h3 className="warranty-heading">Warranty Terms</h3>
    <ol className="warranty-list">
      <li>2 - Year warranty from the date of purchase.</li>
    </ol>
    <p className="warranty-note">
      <strong>Note:</strong> No conversion is required for this stove. Take it
      out of the box and install it.
    </p>
  </div>
);

/* ─── Informational Tooltip Component ─────────────────────────────────────── */
const FieldInfoTooltip = ({ text, ariaLabel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <span
      ref={wrapperRef}
      className="info-tooltip-wrapper"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="info-tooltip-trigger"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen((prev) => !prev);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
      >
        <Info size={15} />
      </button>
      {isOpen && (
        <div className="info-tooltip-card" role="tooltip">
          {text}
        </div>
      )}
    </span>
  );
};

const INDIAN_STATES = [

  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttarakhand", "Uttar Pradesh", "West Bengal",
  "Delhi", "Chandigarh", "Jammu & Kashmir", "Ladakh", "Puducherry"
];

const getExpectedDeliveryDate = (daysToAdd = 7) => {
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + daysToAdd);

  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const dayName = days[targetDate.getDay()];
  const dayNum = targetDate.getDate();
  const monthName = months[targetDate.getMonth()];
  const year = targetDate.getFullYear();

  return `${dayName}, ${dayNum} ${monthName} ${year}`;
};

function ProductPage() {
  const [pincodeInput, setPincodeInput] = useState("");
  const [checkedPincode, setCheckedPincode] = useState("");
  const [pincodeChecked, setPincodeChecked] = useState(false);
  const [pincodeError, setPincodeError] = useState("");

  const handleCheckPincode = () => {
    const clean = pincodeInput.trim();
    if (clean.length === 6 && /^\d+$/.test(clean)) {
      setCheckedPincode(clean);
      setPincodeChecked(true);
      setPincodeError("");
    } else {
      setPincodeChecked(false);
      setPincodeError("Please enter a valid 6-digit PIN code.");
    }
  };
  const { productId: pathProductId } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const queryProductId = searchParams.get("productId") || searchParams.get("sku");
  const targetId = queryProductId || pathProductId || "png-stove";

  // Find product by base ID or variation SKU/ID
  const product = React.useMemo(() => {
    let pMatch = products.find((p) => p.id === targetId);
    if (!pMatch) {
      for (const p of products) {
        if (p.variations) {
          const vMatch = p.variations.find(v => v.sku === targetId || `${p.id}-${v.sku}` === targetId);
          if (vMatch) {
            pMatch = p;
            break;
          }
        }
      }
    }
    return pMatch || products.find((p) => p.id === "png-stove") || products[0];
  }, [targetId]);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [materialFilter, setMaterialFilter] = useState(null);
  const [burnerFilter, setBurnerFilter] = useState(null);
  const [filteredVariations, setFilteredVariations] = useState([]);
  const [showCustomization, setShowCustomization] = useState(false);
  const [previewFreeItem, setPreviewFreeItem] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const galleryRef = React.useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (lightboxOpen) {
          setLightboxOpen(false);
          setZoomLevel(1);
        }
        setPreviewFreeItem(null);
      } else if (lightboxOpen) {
        const activeImages = (selectedVariation?.images && selectedVariation.images.includes(lightboxImage))
          ? selectedVariation.images
          : (realFlameImages.includes(lightboxImage) ? realFlameImages : (selectedVariation?.images || []));
        if (activeImages.length > 1) {
          if (e.key === "ArrowLeft") {
            const idx = activeImages.indexOf(lightboxImage);
            if (idx > 0) {
              const nextImg = activeImages[idx - 1];
              setLightboxImage(nextImg);
              if (selectedVariation?.images?.includes(nextImg)) setMainImage(nextImg);
              setZoomLevel(1);
            }
          } else if (e.key === "ArrowRight") {
            const idx = activeImages.indexOf(lightboxImage);
            if (idx !== -1 && idx < activeImages.length - 1) {
              const nextImg = activeImages[idx + 1];
              setLightboxImage(nextImg);
              if (selectedVariation?.images?.includes(nextImg)) setMainImage(nextImg);
              setZoomLevel(1);
            }
          }
        }
      }
    };
    if (previewFreeItem || lightboxOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    if (lightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [previewFreeItem, lightboxOpen, lightboxImage, selectedVariation]);

  const [extraCartItems, setExtraCartItems] = useState([]);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [modalQuantity, setModalQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("Razorpay"); // 'Razorpay' | 'COD'
  const [priceCatalog, setPriceCatalog] = useState({});

  useEffect(() => {
    let isMounted = true;
    getProductPrices().then((catalog) => {
      if (isMounted && catalog) {
        setPriceCatalog(catalog);
      }
    });
    return () => { isMounted = false; };
  }, []);

  const computeModalPayableAmount = () => {
    const primaryPricing = getPriceForSku(
      priceCatalog,
      selectedVariation?.sku,
      selectedVariation?.mrp,
      selectedVariation?.price
    );
    const primaryTotal = primaryPricing.effectivePrice * modalQuantity;

    const extraTotal = extraCartItems.reduce((sum, item) => {
      const itemPricing = getPriceForSku(
        priceCatalog,
        item.variation?.sku,
        item.variation?.mrp,
        item.variation?.price
      );
      return sum + (itemPricing.effectivePrice * item.quantity);
    }, 0);

    return Math.max(0, primaryTotal + extraTotal);
  };

  const [buyFormData, setBuyFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    houseNo: "",
    street: "",
    landmark: "",
    deliveryInstruction: "",
    city: "",

    state: "",
    pincode: "",
  });
  const [buyFormErrors, setBuyFormErrors] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const validateBuyModalForm = () => {
    const errors = {};
    if (!buyFormData.fullName.trim()) errors.fullName = "Full name is required";
    if (!buyFormData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(buyFormData.email)) {
      errors.email = "Enter a valid email address";
    }
    if (!buyFormData.phone.trim() || buyFormData.phone.trim().length < 10) {
      errors.phone = "Please enter a valid 10-digit mobile number.";
    }
    if (!buyFormData.houseNo.trim()) errors.houseNo = "House/Flat No. is required";
    if (!buyFormData.street.trim()) errors.street = "Street/Area is required";
    if (!buyFormData.city.trim()) errors.city = "City is required";
    if (!buyFormData.state.trim()) errors.state = "Select a state";
    if (!buyFormData.pincode.trim() || buyFormData.pincode.trim().length < 6) {
      errors.pincode = "Please enter a valid 6-digit PIN code.";
    }
    return errors;
  };


  const buildDeliveryAddress = () => {
    const parts = [
      buyFormData.houseNo.trim(),
      buyFormData.street.trim(),
    ];
    if (buyFormData.landmark.trim()) parts.push(buyFormData.landmark.trim());
    parts.push(buyFormData.city.trim());
    parts.push(`${buyFormData.state.trim()} - ${buyFormData.pincode.trim()}`);
    return parts.join(',\n');
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      // SDK already loaded and ready to use
      if (window.Razorpay) { resolve(true); return; }

      const existingScript = document.getElementById("razorpay-sdk");
      if (existingScript) {
        // Tag exists but SDK may still be downloading (e.g. slow mobile
        // network) — wait for it to actually finish instead of assuming
        // it's ready.
        existingScript.addEventListener("load", () => resolve(true));
        existingScript.addEventListener("error", () => resolve(false));
        return;
      }

      const script = document.createElement("script");
      script.id = "razorpay-sdk";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleContinueToPayment = async (e) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;

    const validationErrors = validateBuyModalForm();
    if (Object.keys(validationErrors).length > 0) {
      setBuyFormErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    let userId = 1;
    const deliveryAddress = buildDeliveryAddress();

    // Step 1: POST /api/user/create
    try {
      const userData = await createUser({
        fullName: buyFormData.fullName,
        email: buyFormData.email,
        phone: buyFormData.phone,
        address: deliveryAddress,
      });
      if (userData && userData.userId) userId = userData.userId;
    } catch (err) {
      console.log("User create API fallback", err);
    }

    // Step 2: Calculate multi-product total using dynamic configured pricing
    const primaryPricing = getPriceForSku(
      priceCatalog,
      selectedVariation?.sku,
      selectedVariation?.mrp,
      selectedVariation?.price
    );
    const primaryTotal = primaryPricing.effectivePrice * modalQuantity;
    const extraTotal = extraCartItems.reduce((sum, item) => {
      const itemPricing = getPriceForSku(
        priceCatalog,
        item.variation?.sku,
        item.variation?.mrp,
        item.variation?.price
      );
      return sum + (itemPricing.effectivePrice * item.quantity);
    }, 0);

    const totalQuantityCount = modalQuantity + extraCartItems.reduce((s, i) => s + i.quantity, 0);
    const totalAmount = Math.max(0, primaryTotal + extraTotal);
    const receiptId = `ORD-${paymentMethod === "COD" ? "COD-" : ""}${Date.now().toString().slice(-8)}`;

    const orderTitle = extraCartItems.length > 0
      ? `${product?.baseTitle || "RealFlame Product"} + ${extraCartItems.length} item(s)`
      : (product?.baseTitle || "RealFlame Product");

    const itemDetails = [
      `${product?.baseTitle} (${selectedVariation?.title || selectedVariation?.sku || ""}) x${modalQuantity}`,
      ...extraCartItems.map(i => `${i.productTitle} (${i.variation.title || i.variation.sku || ""}) x${i.quantity}`)
    ].join(", ");

    const orderPayload = {
      userId,
      productSku: selectedVariation?.sku,
      productName: orderTitle,
      productModel: itemDetails,
      quantity: totalQuantityCount,
      amount: totalAmount,
      currency: "INR",
      receipt: receiptId,
      deliveryAddress: deliveryAddress,
      deliveryInstruction: buyFormData.deliveryInstruction.trim() || null,
      couponCode: null,
      deliveryPin: buyFormData.pincode.trim(),
      paymentMethod: paymentMethod
    };

    // ── Cash on Delivery (COD) Flow: Bypasses Razorpay completely ────────
    if (paymentMethod === "COD") {
      try {
        console.log("[COD Order] Placing order with payload:", orderPayload);
        const codOrder = await createOrder(orderPayload);
        console.log("[COD Order] Success response:", codOrder);

        setIsSubmitting(false);
        setSubmitError("");
        setShowBuyModal(false);

        navigate("/payment-success", {
          state: {
            isCod: true,
            paymentMethod: "COD",
            orderNumber: codOrder?.dbOrderNumber || codOrder?.receipt,
            orderId: codOrder?.dbOrderId,
            amount: codOrder?.authoritativeAmount || totalAmount,
            customerName: buyFormData.fullName,
            email: buyFormData.email
          }
        });
        return;
      } catch (err) {
        console.error("Create COD order failed", err);
        setSubmitError(err?.response?.data?.message || "Failed to place Cash on Delivery order. Please try again.");
        setIsSubmitting(false);
        return;
      }
    }

    // ── Online Payment (Razorpay) Flow ──────────────────────────────────
    let razorpayOrder = null;
    try {
      razorpayOrder = await createOrder(orderPayload);
      console.log("[createOrder Online] response:", razorpayOrder);
    } catch (err) {
      console.error("Create order failed", err);
      setSubmitError(err?.response?.data?.message || "Failed to create payment order. Please try again.");
      setIsSubmitting(false);
      return;
    }

    // Step 3: Load Razorpay SDK and open checkout
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setSubmitError("Could not load payment gateway. Please try again.");
      setIsSubmitting(false);
      return;
    }

    const rawPhone = buyFormData.phone ? buyFormData.phone.toString() : "";
    const digitsOnly = rawPhone.replace(/\D/g, "");
    const cleanPhone = digitsOnly.length >= 10 ? digitsOnly.slice(-10) : digitsOnly;

    const options = {
      key: razorpayOrder.keyId || "",
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency || "INR",
      name: "RealFlame",
      description: `${product?.baseTitle || "Product"} × ${modalQuantity}`,
      order_id: razorpayOrder.orderId || razorpayOrder.id,
      prefill: {
        name: buyFormData.fullName,
        email: buyFormData.email,
        contact: cleanPhone ? `+91${cleanPhone}` : "",
      },
      retry: {
        enabled: true,
      },
      theme: { color: "#ff6200" },
      handler: async (response) => {
        const dbOrderId = razorpayOrder?.dbOrderId || 0;

        // Step 4: Verify Payment
        setSubmitError("Verifying payment…");
        setIsSubmitting(true);

        try {
          console.log("[verifyPayment] orderId:", dbOrderId, "paymentId:", response.razorpay_payment_id);
          await verifyPayment({
            orderId:           dbOrderId,
            razorpayOrderId:   response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
            amount:            razorpayOrder?.authoritativeAmount || totalAmount,
          });
          console.log("[verifyPayment] success");
        } catch (err) {
          console.error("[verifyPayment] failed", err);
          setSubmitError(
            "Payment verification failed. Please contact support with your payment ID: " +
            response.razorpay_payment_id
          );
          setIsSubmitting(false);
          return;
        }

        setIsSubmitting(false);
        setSubmitError("");
        setShowBuyModal(false);

        // Step 5: Redirect to success page
        navigate("/payment-success", {
          state: {
            isCod: false,
            paymentMethod: "Razorpay",
            orderNumber: razorpayOrder?.dbOrderNumber || razorpayOrder?.receipt,
            orderId: dbOrderId,
            amount: razorpayOrder?.authoritativeAmount || totalAmount,
            paymentId: response.razorpay_payment_id
          }
        });
      },
      modal: {
        ondismiss: () => {
          setIsSubmitting(false);
        },
      },
    };

    if (!window.Razorpay) {
      setSubmitError("Payment gateway could not load. Please check your internet connection and try again.");
      setIsSubmitting(false);
      return;
    }

    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        setIsSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      console.error("[Razorpay] failed to open checkout", err);
      setSubmitError("Could not open payment gateway. Please try again.");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadRazorpayScript();
  }, [pathProductId]);

  useEffect(() => {
    if (product && product.variations && product.variations.length > 0) {
      const searchParams = new URLSearchParams(window.location.search);
      const qId = searchParams.get("productId") || searchParams.get("sku") || pathProductId;

      const targetVar = product.variations.find(
        (v) => v.sku === qId || `${product.id}-${v.sku}` === qId
      );

      let currentMat = materialFilter;
      let currentBurner = burnerFilter;

      if (currentMat === null) {
        if (targetVar && targetVar.material) {
          currentMat = targetVar.material;
        } else {
          const uniqueMaterials = [
            ...new Set(product.variations.map((v) => v.material).filter(Boolean)),
          ];
          currentMat = uniqueMaterials.length > 0 ? uniqueMaterials[0] : null;
        }
        setMaterialFilter(currentMat);
      }

      if (currentBurner === null) {
        if (targetVar && targetVar.burnerCount) {
          currentBurner = targetVar.burnerCount.toString();
        } else {
          const uniqueBurnerCounts = [
            ...new Set(product.variations.map((v) => v.burnerCount).filter(Boolean)),
          ].sort((a, b) => a - b);
          currentBurner = uniqueBurnerCounts.length > 0 ? uniqueBurnerCounts[0].toString() : null;
        }
        setBurnerFilter(currentBurner);
      }

      // Filter variations based on material & burner filters
      let filtered = product.variations;
      if (currentMat) {
        filtered = filtered.filter((v) => v.material === currentMat);
      }
      if (currentBurner) {
        filtered = filtered.filter((v) => v.burnerCount === parseInt(currentBurner, 10));
      }

      setFilteredVariations(filtered);

      const activeVar =
        (targetVar && filtered.some((v) => v.sku === targetVar.sku) ? targetVar : null) ||
        (filtered.length > 0 ? filtered[0] : product.variations[0]);

      setSelectedVariation(activeVar);
      if (activeVar && activeVar.images && activeVar.images.length > 0) {
        setMainImage(activeVar.images[0]);
      }
    }
  }, [product, materialFilter, burnerFilter, pathProductId]);

  if (!product) {
    return (
      <div className="ProductPage">
        <div className="container">
          <div className="product-not-found">
            <h2>Product Not Found</h2>
            <p>Sorry, the product you are looking for does not exist.</p>
            <button onClick={() => navigate("/products")}>
              View All Products
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleVariationChange = (variation) => {
    setSelectedVariation(variation);
    setMainImage(variation.images[0]);
  };
  // Get unique materials and burner counts from variations
  const uniqueMaterials = [
    ...new Set(product.variations.map((v) => v.material).filter(Boolean)),
  ];
  const uniqueBurnerCounts = [
    ...new Set(product.variations.map((v) => v.burnerCount).filter(Boolean)),
  ].sort((a, b) => a - b);

  const handleMaterialFilter = (material) => {
    setMaterialFilter(material);
  };

  const handleBurnerFilter = (count) => {
    setBurnerFilter(count);
  };

  const handleOpenLightbox = (img) => {
    if (!img) return;
    setMainImage(img);
    setLightboxImage(img);
    setLightboxOpen(true);
    setZoomLevel(1);
  };

  // ── SEO ──────────────────────────────────────────────────────────────────
  const seo = getProductSEO(product, selectedVariation);
  const productBreadcrumbs = [
    { name: "Home", url: "https://realflame.co.in/" },
    { name: "Products", url: "https://realflame.co.in/products" },
    { name: product.baseTitle, url: seo.canonical },
  ];
  const productJsonLd = selectedVariation
    ? productSchema({
        name: seo.title.replace(" | RealFlame", ""),
        description: seo.description,
        image: seo.ogImage,
        price: selectedVariation.price,
        mrp: selectedVariation.mrp,
        sku: selectedVariation.sku,
        url: seo.canonical,
        rating: selectedVariation.rating,
        reviewCount: selectedVariation.reviews,
      })
    : null;
  // ─────────────────────────────────────────────────────────────────────────

  const currentVarImages = (selectedVariation && selectedVariation.images) ? selectedVariation.images.filter(Boolean) : [];
  const currentImageIdx = currentVarImages.indexOf(mainImage);

  const handlePrevSlide = (e) => {
    if (e) e.stopPropagation();
    if (currentVarImages.length > 1) {
      const prevIdx = (currentImageIdx - 1 + currentVarImages.length) % currentVarImages.length;
      setMainImage(currentVarImages[prevIdx]);
    }
  };

  const handleNextSlide = (e) => {
    if (e) e.stopPropagation();
    if (currentVarImages.length > 1) {
      const nextIdx = (currentImageIdx + 1) % currentVarImages.length;
      setMainImage(currentVarImages[nextIdx]);
    }
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 40) {
      handleNextSlide();
    } else if (distance < -40) {
      handlePrevSlide();
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  return (
    <>
      <SEOHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={seo.canonical}
        ogImage={seo.ogImage}
        ogType="product"
        structuredData={[
          breadcrumbSchema(productBreadcrumbs),
          ...(productJsonLd ? [productJsonLd] : []),
        ]}
      />
      <div className="ProductPage">
        <div className="product-main-content">
          {/* Left: Gallery (Thumbnails on left + One Main Image on right) */}
          <div className="product-gallery" ref={galleryRef}>
            {/* Left-side scrollable vertical thumbnails */}
            <div className="product-thumbnails">
              {selectedVariation &&
                selectedVariation.images &&
                selectedVariation.images.map((img, idx) => (
                  img ? (
                    <img
                      key={idx}
                      src={img}
                      alt={`${product.baseTitle}${selectedVariation ? " - " + selectedVariation.title : ""} thumbnail ${idx + 1}`}
                      className={mainImage === img ? "active" : ""}
                      onClick={() => setMainImage(img)}
                      title={`Select Image ${idx + 1}`}
                    />
                  ) : null
                ))}
            </div>

            {/* Single Large Main Product Image Card with Slide Controls */}
            <div className="product-main-image-container">
              {mainImage && (
                <div
                  className="product-main-image-card"
                  onClick={() => handleOpenLightbox(mainImage)}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  title="Click to enlarge image"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenLightbox(mainImage);
                    }
                  }}
                >
                  {/* Left Arrow Button */}
                  {currentVarImages.length > 1 && (
                    <button
                      type="button"
                      className="slider-arrow-btn prev"
                      onClick={handlePrevSlide}
                      aria-label="Previous Image"
                      title="Previous Image"
                    >
                      ‹
                    </button>
                  )}

                  <img
                    src={mainImage}
                    alt={`${product.baseTitle}${selectedVariation ? " - " + selectedVariation.title : ""}`}
                    className="product-main-image"
                  />

                  {/* Right Arrow Button */}
                  {currentVarImages.length > 1 && (
                    <button
                      type="button"
                      className="slider-arrow-btn next"
                      onClick={handleNextSlide}
                      aria-label="Next Image"
                      title="Next Image"
                    >
                      ›
                    </button>
                  )}

                  {/* Slider Dots */}
                  {currentVarImages.length > 1 && (
                    <div className="slider-dots-container" onClick={(e) => e.stopPropagation()}>
                      {currentVarImages.map((img, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`slider-dot ${mainImage === img ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMainImage(img);
                          }}
                          aria-label={`Go to slide ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Details */}
          <div className="product-details">
            <h1 className="product-title">{product.baseTitle}</h1>

            {/* Filter Options Card */}
            {(uniqueMaterials.length > 0 || uniqueBurnerCounts.length > 0) && (
              <div className="product-filters-card">
                {/* Material Filter */}
                {uniqueMaterials.length > 0 && (
                  <div className="filter-group">
                    <span className="filter-label">Material</span>
                    <div className="filter-buttons">
                      {uniqueMaterials.map((material) => {
                        const isActive = materialFilter === material;
                        return (
                          <button
                            key={material}
                            type="button"
                            className={`filter-btn ${isActive ? "active" : ""}`}
                            onClick={() => handleMaterialFilter(material)}
                          >
                            {isActive && <span className="filter-btn-check">✓</span>}
                            <span>{material}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Burner Count Filter */}
                {uniqueBurnerCounts.length > 0 && (
                  <div className="filter-group">
                    <span className="filter-label">Burners</span>
                    <div className="filter-buttons">
                      {uniqueBurnerCounts.map((count) => {
                        const isActive = burnerFilter === count.toString();
                        return (
                          <button
                            key={count}
                            type="button"
                            className={`filter-btn ${isActive ? "active" : ""}`}
                            onClick={() => handleBurnerFilter(count.toString())}
                          >
                            {isActive && <span className="filter-btn-check">✓</span>}
                            <span>{count} Burner{count > 1 ? "s" : ""}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Variation Selection Buttons */}
            {filteredVariations.length > 1 && (
              <div className="variation-selector">
                <h3>Select Variation:</h3>
                <div className="variation-buttons">
                  {filteredVariations.map((variation) => (
                    <button
                      key={variation.sku}
                      className={`variation-btn ${selectedVariation?.sku === variation.sku ? "active" : ""}`}
                      onClick={() => handleVariationChange(variation)}
                    >
                      {variation.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Variation Details */}
            {selectedVariation && (
              <>
                <div className="product-rating-row">
                  <span className="stars">
                    {"★".repeat(Math.round(selectedVariation.rating))}
                    {"☆".repeat(5 - Math.round(selectedVariation.rating))}
                  </span>
                  <span className="rating-value">
                    {selectedVariation.rating}
                  </span>
                  <span className="reviews">
                    {selectedVariation.reviews} reviews
                  </span>
                </div>

                {/* Product Actions & Pricing */}
                {(() => {
                  const isPngStove = product?.baseCategory === "png-stoves" || product?.id === "png-stove" || product?.baseCategory?.toLowerCase().includes("png");
                  return (
                    <div className="product-actions-container">
                      {/* Price Section — Only rendered for PNG Stoves */}
                      {isPngStove && (
                        (selectedVariation?.mrp || selectedVariation?.price) ? (() => {
                          const pricing = getPriceForSku(
                            priceCatalog,
                            selectedVariation?.sku,
                            selectedVariation?.mrp,
                            selectedVariation?.price
                          );
                          return (
                            <div className="product-price-block">
                              <div className="price-main-display">
                                <span className="price-value">
                                  ₹{pricing.effectivePrice.toLocaleString("en-IN")}
                                </span>
                                {pricing.isDiscountActive && (
                                  <>
                                    <span className="mrp-strike">
                                      MRP ₹{pricing.mrp.toLocaleString("en-IN")}
                                    </span>
                                    <span className="instant-discount-pill">
                                      ⚡ ₹{pricing.discountAmount.toLocaleString("en-IN")} OFF ({pricing.savingsPercentage}%)
                                    </span>
                                  </>
                                )}
                              </div>
                              <span className="price-subtext">Inclusive of all taxes • Free Delivery</span>
                            </div>
                          );
                        })() : selectedVariation?.price === "Quote on Request" ? (
                          <div className="product-price-display quote">Price on Request</div>
                        ) : null
                      )}

                      {/* Buy Now Button — ONLY for PNG Stoves */}
                      {isPngStove ? (
                        <button
                          className="buy-now-action-btn"
                          onClick={() => setShowBuyModal(true)}
                        >
                           BUY NOW
                        </button>
                      ) : (
                        <button
                          className="buy-now-action-btn secondary"
                          onClick={() => navigate("/contact-us")}
                        >
                          Contact Us
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Static PIN Code & Expected Delivery Checker */}
                <div className="product-pincode-delivery-card">
                  <div className="pincode-card-header">
                    <span className="pincode-card-icon">🚚</span>
                    <span className="pincode-card-title">Check Delivery Options & Expected Date</span>
                  </div>
                  <div className="pincode-input-row">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Enter 6-digit PIN Code"
                      maxLength={6}
                      value={pincodeInput}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setPincodeInput(val);
                        if (val.length !== 6) {
                          setPincodeChecked(false);
                          setPincodeError("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCheckPincode();
                        }
                      }}
                      className="pincode-input-field"
                    />
                    <button
                      type="button"
                      onClick={handleCheckPincode}
                      className="pincode-check-btn"
                    >
                      CHECK
                    </button>
                  </div>

                  {pincodeChecked && (
                    <div className="delivery-result-box success">
                      <div className="delivery-date-headline">
                        <span>Expected Delivery by <strong>{getExpectedDeliveryDate(7)}</strong></span>
                      </div>
                      <span className="delivery-subtext">Shipping available for PIN {checkedPincode} • Delivered within 5–7 days</span>
                    </div>
                  )}

                  {pincodeError && (
                    <div className="delivery-result-box error">
                      <span>{pincodeError}</span>
                    </div>
                  )}
                </div>

                {/* Free With This Product Section — Only for PNG Stoves */}
                {(product?.baseCategory === "png-stoves" || product?.id === "png-stove" || product?.baseCategory?.toLowerCase().includes("png")) && selectedVariation?.freeItems && selectedVariation.freeItems.length > 0 && (
                  <div className="free-items-section">
                    <h3 className="free-items-title">
                      <span className="free-gift-icon">🎁</span> Free With This Product
                    </h3>
                    <div className="free-items-grid">
                      {selectedVariation.freeItems.map((item) => (
                        <div
                          key={item.id}
                          className="free-item-card clickable"
                          onClick={() => setPreviewFreeItem(item)}
                          title="Click to zoom preview"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setPreviewFreeItem(item);
                            }
                          }}
                        >
                          <div className="free-item-img-container">
                            {item.image && <img src={item.image} alt={item.name} />}
                            <span className="free-item-zoom-hint">🔍 Zoom</span>
                          </div>
                          <div className="free-item-details">
                            <span className="free-item-badge">✓ FREE</span>
                            <span className="free-item-name">{item.name}</span>
                            <span className="free-item-qty">Qty: {item.quantity}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
               

                {/* Product Description */}
                <div className="product-description">
                  {selectedVariation.description}
                </div>

                {/* Warranty & Installation — shown for all products */}
                <WarrantySection />

                {/* RealFlame Feature Images Showcase */}
                {realFlameImages.length > 0 && (
                  <div className="realflame-showcase-section">
                    {realFlameImages.map((img, idx) => (
                      <div key={idx} className="realflame-showcase-card">
                        <img
                          src={img}
                          alt={`RealFlame Feature ${idx + 1}`}
                          className="realflame-showcase-img"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Conditional rendering based on product category */}
                {product.baseCategory === "customization" ? (
                  // Special layout for customization products - informative style
                  <div className="customization-detailed-info">
                    {selectedVariation.customization && (
                      <>
                        <div className="customization-section">
                          <h3 className="customization-section-title">
                            Customization Features
                          </h3>
                          <ul className="customization-features-list">
                            {selectedVariation.customization.features.map(
                              (feature, idx) => (
                                <li key={idx}>{feature}</li>
                              ),
                            )}
                          </ul>
                        </div>

                        {selectedVariation.customization.limitations && (
                          <div className="customization-section">
                            <h3 className="customization-section-title">
                              Important Notes
                            </h3>
                            <ul className="customization-features-list">
                              {selectedVariation.customization.limitations.map(
                                (limit, idx) => (
                                  <li key={idx}>{limit}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                        {selectedVariation.customization.note && (
                          <div className="customization-note">
                            <p>{selectedVariation.customization.note}</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Specifications table for customization */}
                    <div className="customization-section">
                      <h3 className="customization-section-title">
                        Service Details
                      </h3>
                      <div className="product-specs-container">
                        <table className="product-specs-table">
                          <tbody>
                            {selectedVariation.specs.map((spec, idx) => (
                              <tr key={idx}>
                                <td>
                                  <b>{spec.label}</b>
                                </td>
                                <td>{spec.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Standard layout for all other products
                  <>
                    {/* Toggle between Customization and Specifications (for non-customization products) */}
                    {selectedVariation.customization && (
                      <div
                        className="info-toggle-buttons"
                        style={{
                          marginTop: "20px",
                          display: "flex",
                          gap: "10px",
                        }}
                      >
                        <button
                          className={`variation-btn ${!showCustomization ? "active" : ""}`}
                          onClick={() => setShowCustomization(false)}
                        >
                          Specifications
                        </button>
                        <button
                          className={`variation-btn ${showCustomization ? "active" : ""}`}
                          onClick={() => setShowCustomization(true)}
                        >
                          Customization
                        </button>
                      </div>
                    )}

                    {/* Show either customization or specifications based on toggle */}
                    {showCustomization && selectedVariation.customization ? (
                      <div
                        className="customization-info"
                        style={{
                          marginTop: "20px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                        }}
                      >
                        <h3 style={{ marginTop: 0 }}>Customization Features</h3>
                        <ul>
                          {selectedVariation.customization.features.map(
                            (feature, idx) => (
                              <li key={idx}>{feature}</li>
                            ),
                          )}
                        </ul>
                        {selectedVariation.customization.limitations && (
                          <>
                            <h4>Limitations</h4>
                            <ul>
                              {selectedVariation.customization.limitations.map(
                                (limit, idx) => (
                                  <li key={idx}>{limit}</li>
                                ),
                              )}
                            </ul>
                          </>
                        )}
                        {selectedVariation.customization.note && (
                          <p style={{ fontStyle: "italic", marginBottom: 0 }}>
                            {selectedVariation.customization.note}
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="product-specs-title">
                          Product Specifications
                        </div>
                        <div className="product-specs-container">
                          <table className="product-specs-table">
                            <thead>
                              <tr>
                                <th>Description</th>
                                <th>Specifications</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedVariation.specs.map((spec, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <b>{spec.label}</b>
                                  </td>
                                  <td>{spec.value}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Product Information Section */}
        <div className="product-info-section">
          <div className="container">
            <h2>About {product.baseTitle}</h2>
            <p>{product.baseDescription}</p>

            {filteredVariations.length > 1 && (
              <div className="variations-overview">
                <h3>Available Variations</h3>
                <div className="variations-grid">
                  {filteredVariations.map((variation) => (
                    <div key={variation.sku} className="variation-card">
                      <h4>{variation.title}</h4>
                      <p className="variation-description">
                        {variation.description}
                      </p>
                      {variation.material && (
                        <p className="variation-material">
                          Material: {variation.material}
                        </p>
                      )}
                      {variation.burnerCount && (
                        <p className="variation-burners">
                          Burners: {variation.burnerCount}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Free Item Lightbox / Zoom Preview Modal */}
        {previewFreeItem && (
          <div
            className="free-item-modal-backdrop"
            onClick={() => setPreviewFreeItem(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`Preview ${previewFreeItem.name}`}
          >
            <div
              className="free-item-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="free-item-modal-close"
                onClick={() => setPreviewFreeItem(null)}
                aria-label="Close preview"
              >
                ×
              </button>
              <div className="free-item-modal-header">
                <span className="free-item-modal-badge">✓ INCLUDED FREE</span>
                <h3 className="free-item-modal-title">{previewFreeItem.name}</h3>
                <span className="free-item-modal-qty">Included Quantity: {previewFreeItem.quantity}</span>
              </div>
              <div className="free-item-modal-img-wrap">
                {previewFreeItem.image && <img src={previewFreeItem.image} alt={previewFreeItem.name} />}
              </div>
              <div className="free-item-modal-footer">
                <span>Click anywhere outside or press <kbd>ESC</kbd> to close</span>
              </div>
            </div>
          </div>
        )}

        {/* RealFlame Complete Your Details Modal */}
        {showBuyModal && (
          <div 
            className="buy-modal-backdrop" 
            onClick={(e) => {
              if (e.target.classList.contains('buy-modal-backdrop') && !isSubmitting) setShowBuyModal(false);
            }}
          >
            <div className="buy-modal-content">
              <div className="modal-drag-handle"></div>
              <button 
                type="button" 
                className="buy-modal-close" 
                onClick={() => !isSubmitting && setShowBuyModal(false)}
                aria-label="Close"
              >
                ×
              </button>

              <div className="modal-header">
                <h2 className="modal-title">Complete Your Details</h2>
                <p className="modal-subtitle">Fill in your details to proceed to payment.</p>
              </div>

              {submitError && (
                <div className="rf-error-alert">
                  <p>{submitError}</p>
                </div>
              )}

              {/* Product Summary Card + Order Summary */}
              {(() => {
                const primaryPricing = getPriceForSku(
                  priceCatalog,
                  selectedVariation?.sku,
                  selectedVariation?.mrp,
                  selectedVariation?.price
                );

                const primaryCartItem = {
                  id: `primary_${selectedVariation?.sku || 'item'}`,
                  title: product?.baseTitle || "RealFlame Product",
                  model: selectedVariation?.title || selectedVariation?.sku || "Standard",
                  material: selectedVariation?.material,
                  unitPrice: primaryPricing.effectivePrice,
                  mrp: primaryPricing.mrp,
                  unitDiscount: primaryPricing.discountAmount,
                  isDiscountActive: primaryPricing.isDiscountActive,
                  quantity: modalQuantity,
                  image: mainImage || selectedVariation?.images[0] || product.baseImage,
                  freeItems: selectedVariation?.freeItems || [],
                  isPrimary: true
                };

                const extraItemsFormatted = extraCartItems.map(item => {
                  const itemPricing = getPriceForSku(
                    priceCatalog,
                    item.variation?.sku,
                    item.variation?.mrp,
                    item.variation?.price
                  );
                  return {
                    id: item.id,
                    title: item.productTitle,
                    model: item.variation?.title || item.variation?.sku || "Standard",
                    material: item.variation?.material,
                    unitPrice: itemPricing.effectivePrice,
                    mrp: itemPricing.mrp,
                    unitDiscount: itemPricing.discountAmount,
                    isDiscountActive: itemPricing.isDiscountActive,
                    quantity: item.quantity,
                    image: item.variation?.images?.[0] || item.baseImage,
                    freeItems: item.variation?.freeItems || [],
                    isPrimary: false,
                    rawItem: item
                  };
                });

                const allOrderItems = [primaryCartItem, ...extraItemsFormatted];

                const totalMrpAmount = allOrderItems.reduce((sum, item) => sum + (item.mrp * item.quantity), 0);
                const totalDiscountAmount = allOrderItems.reduce((sum, item) => sum + (item.unitDiscount * item.quantity), 0);
                const finalPayableAmount = Math.max(0, totalMrpAmount - totalDiscountAmount);
                const totalStoveQuantity = allOrderItems.reduce((sum, item) => sum + item.quantity, 0);

                return (
                  <>
                    {/* Multi-Product Cards List in Modal */}
                    <div className="modal-cart-items-list">
                      {allOrderItems.map((cartItem) => (
                        <div key={cartItem.id} className="product-summary-card">
                          <div className="summary-image-container">
                            {cartItem.image && <img src={cartItem.image} alt={cartItem.title} />}
                          </div>
                          <div className="summary-details">
                            <h3 className="summary-title">{cartItem.title}</h3>
                            <p className="summary-meta"><strong>Model:</strong> {cartItem.model}</p>
                            {cartItem.material && (
                              <p className="summary-meta"><strong>Material:</strong> {cartItem.material}</p>
                            )}
                            <div className="summary-price-row">
                              <span className="summary-item-price">₹{(cartItem.unitPrice * cartItem.quantity).toLocaleString("en-IN")}</span>
                              {cartItem.isDiscountActive && (
                                <span className="mrp-strike" style={{ marginLeft: "8px", fontSize: "0.85rem" }}>
                                  MRP ₹{(cartItem.mrp * cartItem.quantity).toLocaleString("en-IN")}
                                </span>
                              )}
                            </div>

                            {/* Quantity selector */}
                            <div className="summary-qty-row">
                              <span className="summary-qty-label">Qty:</span>
                              <div className="quantity-selector">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (cartItem.isPrimary) {
                                      setModalQuantity(Math.max(1, modalQuantity - 1));
                                    } else {
                                      setExtraCartItems(prev => prev.map(e => e.id === cartItem.id ? { ...e, quantity: e.quantity - 1 } : e).filter(e => e.quantity > 0));
                                    }
                                  }}
                                  disabled={isSubmitting || (cartItem.isPrimary && modalQuantity <= 1)}
                                >
                                  -
                                </button>
                                <span>{cartItem.quantity}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (cartItem.isPrimary) {
                                      setModalQuantity(modalQuantity + 1);
                                    } else {
                                      setExtraCartItems(prev => prev.map(e => e.id === cartItem.id ? { ...e, quantity: e.quantity + 1 } : e));
                                    }
                                  }}
                                  disabled={isSubmitting}
                                >
                                  +
                                </button>
                              </div>

                              {!cartItem.isPrimary && (
                                <button
                                  type="button"
                                  className="remove-cart-item-btn"
                                  onClick={() => setExtraCartItems(prev => prev.filter(e => e.id !== cartItem.id))}
                                  disabled={isSubmitting}
                                >
                                  Remove
                                </button>
                              )}
                            </div>

                            {/* Included Free Items under this product */}
                            {cartItem.freeItems && cartItem.freeItems.length > 0 && (
                              <div className="modal-free-items-container mini">
                                <span className="modal-free-items-heading">🎁 Free Items ({cartItem.freeItems.length}):</span>
                                <div className="modal-free-items-list">
                                  {cartItem.freeItems.map((fItem) => (
                                    <div
                                      key={fItem.id}
                                      className="modal-free-item-card clickable"
                                      onClick={() => setPreviewFreeItem(fItem)}
                                      title="Click to zoom preview"
                                      role="button"
                                      tabIndex={0}
                                    >
                                      <div className="modal-free-item-img-wrapper">
                                        {fItem.image && <img src={fItem.image} alt={fItem.name} />}
                                      </div>
                                      <span className="modal-free-item-name">{fItem.name}</span>
                                      <span className="modal-free-item-qty">Qty: {fItem.quantity * cartItem.quantity}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Order Summary Section */}
                    {totalMrpAmount > 0 && (
                      <div className="order-summary-section">
                        <h4 className="order-summary-title">Order Summary ({allOrderItems.length} Product{allOrderItems.length > 1 ? 's' : ''})</h4>
                        <div className="order-summary-rows">
                          {allOrderItems.map((item) => (
                            <div key={item.id} className="order-summary-row item-detail-row">
                              <span className="order-summary-key">{item.title} ({item.model}) × {item.quantity}</span>
                              <span className="order-summary-val">₹{(item.mrp * item.quantity).toLocaleString("en-IN")}</span>
                            </div>
                          ))}
                          <div className="order-summary-divider" />

                          {totalDiscountAmount > 0 && (
                            <div className="order-summary-row green-row">
                              <span className="order-summary-key">Discount Savings ({totalStoveQuantity} Item{totalStoveQuantity > 1 ? 's' : ''})</span>
                              <span className="order-summary-val discount-val">- ₹{totalDiscountAmount.toLocaleString("en-IN")}</span>
                            </div>
                          )}

                          <div className="order-summary-row green-row">
                            <span className="order-summary-key">Delivery Charges</span>
                            <span className="order-summary-val free-val"><del>₹99</del>FREE</span>
                          </div>

                          <div className="order-summary-divider" />
                          <div className="order-summary-row total-row">
                            <span className="order-summary-total-label">Total Payable Amount</span>
                            <span className="order-summary-total-val">₹{finalPayableAmount.toLocaleString("en-IN")}</span>
                          </div>

                          {totalDiscountAmount > 0 && (
                            <div className="order-savings-pill">
                              🎉 Woohoo! You save <strong>₹{totalDiscountAmount.toLocaleString("en-IN")}</strong> on this order!
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}


              {paymentSuccess ? (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{ fontSize: "3rem", marginBottom: "12px" }}>🎉</div>
                  <h3 style={{ color: "#10b981", margin: "0 0 8px" }}>Payment Successful!</h3>
                  <p style={{ color: "#555", margin: "0 0 20px" }}>Thank you for your order. You will receive a confirmation email shortly.</p>
                  <button
                    type="button"
                    className="product-action-btn primary"
                    onClick={() => { setShowBuyModal(false); setPaymentSuccess(false); }}
                  >
                    Close
                  </button>
                </div>
              ) : (
              <form onSubmit={handleContinueToPayment} noValidate>
                {/* Customer Information Section */}
                <div className="modal-form-section">
                  <h3 className="section-heading">Customer Information</h3>

                  <div className="input-group full-width">
                    <label>Full Name <span className="required-star">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      value={buyFormData.fullName}
                      onChange={(e) => {
                        setBuyFormData({ ...buyFormData, fullName: e.target.value });
                        if (buyFormErrors.fullName) setBuyFormErrors({ ...buyFormErrors, fullName: undefined });
                      }}
                      className={`form-input-full ${buyFormErrors.fullName ? 'input-error' : ''}`}
                      disabled={isSubmitting}
                    />
                    {buyFormErrors.fullName && <span className="error-text">{buyFormErrors.fullName}</span>}
                  </div>

                  <div className="form-row">
                    <div className="input-group">
                      <label className="label-with-info">
                        <span>Email Address <span className="required-star">*</span></span>
                        <FieldInfoTooltip
                          ariaLabel="More information about email address"
                          text="Please enter a valid email address. This email will be used to send your order confirmation, payment receipt, and future order updates."
                        />
                      </label>
                      <input
                        type="email"
                        placeholder="example@gmail.com"
                        value={buyFormData.email}
                        onChange={(e) => {
                          setBuyFormData({ ...buyFormData, email: e.target.value });
                          if (buyFormErrors.email) setBuyFormErrors({ ...buyFormErrors, email: undefined });
                        }}
                        className={`form-input-full ${buyFormErrors.email ? 'input-error' : ''}`}
                        disabled={isSubmitting}
                      />
                      {buyFormErrors.email && <span className="error-text">{buyFormErrors.text || buyFormErrors.email}</span>}
                    </div>

                    <div className="input-group">
                      <label className="label-with-info">
                        <span>Mobile Number <span className="required-star">*</span></span>
                        <FieldInfoTooltip
                          ariaLabel="More information about mobile number"
                          text="Please enter a valid 10-digit mobile number. This number will be used for delivery updates and customer support."
                        />
                      </label>
                      <div className="phone-container">
                        <div className="country-code">+91</div>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="tel-national"
                          placeholder="9876543210"
                          value={buyFormData.phone}
                          maxLength={10}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setBuyFormData(prev => ({ ...prev, phone: digitsOnly }));
                            if (digitsOnly.length === 10) {
                              setBuyFormErrors(prev => ({ ...prev, phone: undefined }));
                            }
                          }}
                          onBlur={() => {
                            if (buyFormData.phone && buyFormData.phone.length < 10) {
                              setBuyFormErrors(prev => ({ ...prev, phone: "Please enter a valid 10-digit mobile number." }));
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData.getData('text');
                            const digitsOnly = pasted.replace(/\D/g, '').slice(0, 10);
                            setBuyFormData(prev => ({ ...prev, phone: digitsOnly }));
                            if (digitsOnly.length === 10) {
                              setBuyFormErrors(prev => ({ ...prev, phone: undefined }));
                            }
                          }}
                          className={`phone-input ${buyFormErrors.phone ? 'input-error' : ''}`}
                          disabled={isSubmitting}
                        />
                      </div>
                      {buyFormErrors.phone && <span className="error-text">{buyFormErrors.phone}</span>}
                    </div>
                  </div>


                </div>

                {/* Delivery Address Section */}
                <div className="modal-form-section">
                  <h3 className="section-heading">Delivery Address</h3>

                  <div className="form-row">
                    <div className="input-group">
                      <label>House / Flat No. <span className="required-star">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. Flat 302, Sunrise Apts"
                        value={buyFormData.houseNo}
                        onChange={(e) => {
                          setBuyFormData({ ...buyFormData, houseNo: e.target.value });
                          if (buyFormErrors.houseNo) setBuyFormErrors({ ...buyFormErrors, houseNo: undefined });
                        }}
                        className={`form-input ${buyFormErrors.houseNo ? 'input-error' : ''}`}
                        disabled={isSubmitting}
                      />
                      {buyFormErrors.houseNo && <span className="error-text">{buyFormErrors.houseNo}</span>}
                    </div>

                    <div className="input-group">
                      <label>Street / Area <span className="required-star">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. MG Road, Sector 4"
                        value={buyFormData.street}
                        onChange={(e) => {
                          setBuyFormData({ ...buyFormData, street: e.target.value });
                          if (buyFormErrors.street) setBuyFormErrors({ ...buyFormErrors, street: undefined });
                        }}
                        className={`form-input ${buyFormErrors.street ? 'input-error' : ''}`}
                        disabled={isSubmitting}
                      />
                      {buyFormErrors.street && <span className="error-text">{buyFormErrors.street}</span>}
                    </div>
                  </div>

                  <div className="input-group full-width">
                    <label>Landmark (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. Near City Mall"
                      value={buyFormData.landmark}
                      onChange={(e) => setBuyFormData({ ...buyFormData, landmark: e.target.value })}
                      className="form-input-full"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="input-group full-width">
                    <label>Delivery Instruction (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. Call before delivery, Ring the bell twice, Deliver to security gate."
                      value={buyFormData.deliveryInstruction}
                      onChange={(e) => setBuyFormData({ ...buyFormData, deliveryInstruction: e.target.value })}
                      className="form-input-full"
                      disabled={isSubmitting}
                    />
                  </div>


                  <div className="form-row">
                    <div className="input-group">
                      <label>City <span className="required-star">*</span></label>
                      <input
                        type="text"
                        placeholder="e.g. Lucknow"
                        value={buyFormData.city}
                        onChange={(e) => {
                          setBuyFormData({ ...buyFormData, city: e.target.value });
                          if (buyFormErrors.city) setBuyFormErrors({ ...buyFormErrors, city: undefined });
                        }}
                        className={`form-input ${buyFormErrors.city ? 'input-error' : ''}`}
                        disabled={isSubmitting}
                      />
                      {buyFormErrors.city && <span className="error-text">{buyFormErrors.city}</span>}
                    </div>

                    <div className="input-group">
                      <label>State <span className="required-star">*</span></label>
                      <select
                        value={buyFormData.state}
                        onChange={(e) => {
                          setBuyFormData({ ...buyFormData, state: e.target.value });
                          if (buyFormErrors.state) setBuyFormErrors({ ...buyFormErrors, state: undefined });
                        }}
                        className={`form-select ${buyFormErrors.state ? 'input-error' : ''}`}
                        disabled={isSubmitting}
                      >
                        <option value="">Select State</option>
                        {INDIAN_STATES.map((st) => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                      {buyFormErrors.state && <span className="error-text">{buyFormErrors.state}</span>}
                    </div>
                  </div>

                  <div className="input-group full-width">
                    <label>PIN Code <span className="required-star">*</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="postal-code"
                      placeholder="e.g. 226010"
                      value={buyFormData.pincode}
                      maxLength={6}
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setBuyFormData(prev => ({ ...prev, pincode: digitsOnly }));
                        if (digitsOnly.length === 6) {
                          setBuyFormErrors(prev => ({ ...prev, pincode: undefined }));
                        }
                      }}
                      onBlur={() => {
                        if (buyFormData.pincode && buyFormData.pincode.length < 6) {
                          setBuyFormErrors(prev => ({ ...prev, pincode: "Please enter a valid 6-digit PIN code." }));
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData('text');
                        const digitsOnly = pasted.replace(/\D/g, '').slice(0, 6);
                        setBuyFormData(prev => ({ ...prev, pincode: digitsOnly }));
                        if (digitsOnly.length === 6) {
                          setBuyFormErrors(prev => ({ ...prev, pincode: undefined }));
                        }
                      }}
                      className={`form-input-full ${buyFormErrors.pincode ? 'input-error' : ''}`}
                      disabled={isSubmitting}
                    />
                    {buyFormErrors.pincode && <span className="error-text">{buyFormErrors.pincode}</span>}


                    {buyFormData.pincode.trim().length === 6 && (
                      <div className="modal-delivery-date-banner">
                        <span className="delivery-truck-icon">🚚</span>
                        <span>Expected Delivery by <strong>{getExpectedDeliveryDate(7)}</strong> (Free Express Shipping)</span>
                      </div>
                    )}


                  </div>
                </div>


                {/* Also Check Section */}
                <div className="modal-form-section related-slider-section">
                  <h3 className="section-heading">🛒 Also Check</h3>
                  <div className="related-products-slider">
                    {products.flatMap(p => p.variations.map(v => ({ product: p, variation: v })))
                      .filter(({ product: p, variation: v }) => !(p.id === product?.id && v.sku === selectedVariation?.sku) && (typeof v.price === "number" || typeof v.mrp === "number") && (p.baseCategory === "png-stoves" || p.id === "png-stove" || p.baseCategory?.toLowerCase().includes("png")))
                      .map(({ product: relatedProd, variation: v }) => {
                        const itemId = `${relatedProd.id}_${v.sku}`;
                        const addedItem = extraCartItems.find(e => e.id === itemId);
                        const pricing = getPriceForSku(priceCatalog, v.sku, v.mrp, v.price);

                        return (
                          <div key={itemId} className="related-product-card">
                            <div className="related-card-img-wrap">
                              {(v.images?.[0] || relatedProd.baseImage) && (
                                <img src={v.images[0] || relatedProd.baseImage} alt={v.title || relatedProd.baseTitle} />
                              )}
                            </div>

                            <div className="related-card-body">
                              <h4 className="related-card-title">{relatedProd.baseTitle}</h4>
                              <p className="related-card-model">{v.title || v.sku}</p>
                              
                              {v.freeItems && v.freeItems.length > 0 && (
                                <span className="related-free-badge">🎁 Free Gift Included</span>
                              )}

                              <div className="related-card-price-row">
                                <span className="related-selling-price">₹{pricing.effectivePrice.toLocaleString("en-IN")}</span>
                                {pricing.isDiscountActive && (
                                  <del className="related-mrp" style={{ fontSize: '0.8rem', color: '#888', marginLeft: '6px' }}>₹{pricing.mrp.toLocaleString("en-IN")}</del>
                                )}
                              </div>

                              {addedItem ? (
                                <div className="related-qty-controls">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setExtraCartItems(prev => prev.map(e => e.id === itemId ? { ...e, quantity: e.quantity - 1 } : e).filter(e => e.quantity > 0));
                                    }}
                                    disabled={isSubmitting}
                                  >
                                    -
                                  </button>
                                  <span>{addedItem.quantity}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setExtraCartItems(prev => prev.map(e => e.id === itemId ? { ...e, quantity: e.quantity + 1 } : e));
                                    }}
                                    disabled={isSubmitting}
                                  >
                                    +
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="related-add-btn"
                                  onClick={() => {
                                    setExtraCartItems(prev => [
                                      ...prev,
                                      {
                                        id: itemId,
                                        productId: relatedProd.id,
                                        productTitle: relatedProd.baseTitle,
                                        baseImage: relatedProd.baseImage,
                                        variation: v,
                                        quantity: 1
                                      }
                                    ]);
                                  }}
                                  disabled={isSubmitting}
                                >
                                  + ADD
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="modal-form-section payment-method-section">
                  <h3 className="section-heading">Payment Method</h3>
                  <p className="payment-method-desc">Choose your preferred payment method:</p>

                  <div className="payment-options-grid">
                    <label className={`payment-option-card ${paymentMethod === 'Razorpay' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="modalPaymentMethod"
                        value="Razorpay"
                        checked={paymentMethod === 'Razorpay'}
                        onChange={() => setPaymentMethod('Razorpay')}
                        disabled={isSubmitting}
                      />
                      <div className="payment-option-content">
                        <div className="payment-option-header">
                          <span className="payment-option-title">Pay Online</span>
                          <span className="payment-badge online-badge">Instant</span>
                        </div>
                        <p className="payment-option-sub">UPI, Credit/Debit Cards, NetBanking, Wallets</p>
                        <div className="payment-option-extra">Instant confirmation via Razorpay</div>
                      </div>
                    </label>

                    <label className={`payment-option-card ${paymentMethod === 'COD' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="modalPaymentMethod"
                        value="COD"
                        checked={paymentMethod === 'COD'}
                        onChange={() => setPaymentMethod('COD')}
                        disabled={isSubmitting}
                      />
                      <div className="payment-option-content">
                        <div className="payment-option-header">
                          <span className="payment-option-title">Cash on Delivery</span>
                          <span className="payment-badge cod-badge">COD</span>
                        </div>
                        <p className="payment-option-sub">Pay in cash when your stove is delivered</p>
                        <div className="payment-option-extra">No online payment needed now</div>
                      </div>
                    </label>
                  </div>

                  {/* Payment Summary Box */}
                  <div className={`payment-summary-banner ${paymentMethod === 'COD' ? 'cod-banner' : 'online-banner'}`}>
                    <div className="payment-summary-info">
                      <span className="summary-banner-label">Selected Method:</span>
                      <strong className="summary-banner-val">
                        {paymentMethod === 'COD' ? 'Cash on Delivery (COD)' : 'Online Payment (Razorpay)'}
                      </strong>
                    </div>
                    <div className="payment-summary-amount">
                      <span className="summary-banner-label">Payable Amount:</span>
                      <strong className="summary-banner-price">₹{computeModalPayableAmount().toLocaleString("en-IN")}</strong>
                    </div>
                  </div>
                </div>

                {/* Modal Buttons */}
                <div className="modal-buttons-row">
                  <button 
                    type="submit" 
                    className="product-action-btn primary continue-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="spinner-text">
                        <span className="rf-spinner"></span> Processing...
                      </span>
                    ) : paymentMethod === "COD" ? (
                      `PLACE COD ORDER (₹${computeModalPayableAmount().toLocaleString("en-IN")})`
                    ) : (
                      `CONTINUE TO PAY (₹${computeModalPayableAmount().toLocaleString("en-IN")})`
                    )}
                  </button>

                  <button 
                    type="button" 
                    className="product-action-btn secondary cancel-btn"
                    onClick={() => setShowBuyModal(false)}
                    disabled={isSubmitting}
                  >
                    CANCEL
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        )}

        {/* Product Image Lightbox */}
        {lightboxOpen && lightboxImage && (() => {
          const activeImages = (selectedVariation?.images && selectedVariation.images.includes(lightboxImage))
            ? selectedVariation.images
            : (realFlameImages.includes(lightboxImage) ? realFlameImages : (selectedVariation?.images || []));

          return (
            <div
              className="product-lightbox-backdrop"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setLightboxOpen(false);
                  setZoomLevel(1);
                }
              }}
            >
              <div className="product-lightbox-content">
                <button
                  className="product-lightbox-close"
                  onClick={() => {
                    setLightboxOpen(false);
                    setZoomLevel(1);
                  }}
                  aria-label="Close image viewer"
                >
                  &times;
                </button>

                <div className="product-lightbox-middle">
                  {activeImages.length > 1 && (
                    <button
                      className="product-lightbox-prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        const idx = activeImages.indexOf(lightboxImage);
                        if (idx > 0) {
                          const nextImg = activeImages[idx - 1];
                          setLightboxImage(nextImg);
                          if (selectedVariation?.images?.includes(nextImg)) setMainImage(nextImg);
                          setZoomLevel(1);
                        }
                      }}
                      disabled={activeImages.indexOf(lightboxImage) <= 0}
                      aria-label="Previous image"
                    >
                      &#10094;
                    </button>
                  )}

                  <div
                    className="product-lightbox-image-wrapper"
                    onWheel={(e) => {
                      e.preventDefault();
                      setZoomLevel((prev) => {
                        const delta = e.deltaY > 0 ? -0.15 : 0.15;
                        return Math.min(Math.max(Number((prev + delta).toFixed(2)), 0.5), 3.5);
                      });
                    }}
                    onTouchStart={(e) => {
                      if (e.touches && e.touches.length === 1) {
                        setTouchStartPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (e.changedTouches && e.changedTouches.length === 1 && activeImages.length > 1) {
                        const deltaX = e.changedTouches[0].clientX - touchStartPos.x;
                        const deltaY = e.changedTouches[0].clientY - touchStartPos.y;
                        if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
                          const idx = activeImages.indexOf(lightboxImage);
                          if (deltaX < 0 && idx !== -1 && idx < activeImages.length - 1) {
                            const nextImg = activeImages[idx + 1];
                            setLightboxImage(nextImg);
                            if (selectedVariation?.images?.includes(nextImg)) setMainImage(nextImg);
                            setZoomLevel(1);
                          } else if (deltaX > 0 && idx > 0) {
                            const nextImg = activeImages[idx - 1];
                            setLightboxImage(nextImg);
                            if (selectedVariation?.images?.includes(nextImg)) setMainImage(nextImg);
                            setZoomLevel(1);
                          }
                        }
                      }
                    }}
                    onDoubleClick={() => {
                      setZoomLevel((prev) => (prev > 1 ? 1 : 2));
                    }}
                    title="Double click or scroll to zoom"
                  >
                    <img
                      className="product-lightbox-image"
                      src={lightboxImage}
                      alt="Product full view"
                      style={{
                        transform: `scale(${zoomLevel})`,
                      }}
                      draggable={false}
                    />
                  </div>

                  {activeImages.length > 1 && (
                    <button
                      className="product-lightbox-next"
                      onClick={(e) => {
                        e.stopPropagation();
                        const idx = activeImages.indexOf(lightboxImage);
                        if (idx !== -1 && idx < activeImages.length - 1) {
                          const nextImg = activeImages[idx + 1];
                          setLightboxImage(nextImg);
                          if (selectedVariation?.images?.includes(nextImg)) setMainImage(nextImg);
                          setZoomLevel(1);
                        }
                      }}
                      disabled={activeImages.indexOf(lightboxImage) === activeImages.length - 1}
                      aria-label="Next image"
                    >
                      &#10095;
                    </button>
                  )}
                </div>

                <div className="product-lightbox-controls">
                  <button
                    onClick={() => setZoomLevel((prev) => Math.min(prev + 0.25, 3))}
                    aria-label="Zoom in"
                    title="Zoom In"
                  >
                    &#43;
                  </button>
                  <button
                    onClick={() => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))}
                    aria-label="Zoom out"
                    title="Zoom Out"
                  >
                    &#8722;
                  </button>
                  <button
                    onClick={() => setZoomLevel(1)}
                    aria-label="Reset zoom"
                    title="Reset"
                  >
                    &#8634;
                  </button>
                </div>

                {activeImages.length > 1 && (
                  <div className="product-lightbox-thumbnails">
                    {activeImages.map((img, idx) =>
                      img ? (
                        <img
                          key={idx}
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          className={lightboxImage === img ? "active" : ""}
                          onClick={() => {
                            setLightboxImage(img);
                            if (selectedVariation?.images?.includes(img)) setMainImage(img);
                            setZoomLevel(1);
                          }}
                        />
                      ) : null
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Loved by Home Cooks - Verified Ratings Section */}
        <section className="rf-ratings-trust-section">
          <div className="rf-max-container">
            <div className="rf-section-header">
              <span className="rf-section-tag">VERIFIED RATINGS</span>
              <h2 className="rf-section-title">Loved by Home Cooks</h2>
              <p className="rf-section-subtitle">
                Built for heavy Indian cooking. Rated for safety and performance.
              </p>
            </div>

            <div className="rf-ratings-summary-card">
              <div className="rf-score-block">
                <div className="rf-big-score">4.6</div>
                <div className="rf-score-stars">
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="#FF7A00" color="#FF7A00" />
                  <Star size={18} fill="url(#halfStarGradient)" color="#FF7A00" />
                </div>
                <div className="rf-score-text">Based on 89 Product Ratings</div>
                <div className="rf-data-note">* Aggregate ratings calculated across Realflame PNG product range</div>
              </div>

              <div className="rf-rating-bars">
                <div className="rf-bar-row">
                  <span className="rf-bar-label">5 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "70%" }} />
                  </div>
                  <span className="rf-bar-percent">70%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">4 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "20%" }} />
                  </div>
                  <span className="rf-bar-percent">20%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">3 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "6%" }} />
                  </div>
                  <span className="rf-bar-percent">6%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">2 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "3%" }} />
                  </div>
                  <span className="rf-bar-percent">3%</span>
                </div>
                <div className="rf-bar-row">
                  <span className="rf-bar-label">1 ★</span>
                  <div className="rf-bar-track">
                    <div className="rf-bar-fill" style={{ width: "1%" }} />
                  </div>
                  <span className="rf-bar-percent">1%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Moving Hinglish Customer Feedback Carousel */}
        <section className="rf-feedback-marquee-section">
          <div className="rf-marquee-header">
            <h3 className="rf-feedback-title">What Home Cooks Say About Realflame</h3>
          </div>
          <div className="rf-marquee-container">
            <div className="rf-marquee-track">
              {[...customerFeedbackList, ...customerFeedbackList].map((rev, idx) => (
                <div className="rf-feedback-card" key={`rev-${rev.id}-${idx}`}>
                  <div className="rf-feedback-stars">
                    {Array.from({ length: rev.rating }).map((_, i) => (
                      <Star key={i} size={14} fill="#FF7A00" color="#FF7A00" />
                    ))}
                  </div>
                  <p className="rf-feedback-quote">"{rev.quote}"</p>
                  <div className="rf-feedback-author">
                    <span className="rf-author-name">{rev.author}</span>
                    <span className="rf-author-loc">{rev.location}</span>
                  </div>
                  <span className="rf-feedback-prod-badge">{rev.product}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Certifications Section */}
        <section className="certifications-marquee-section-fullwidth">
          <div className="certifications-container">
            <h2 className="marquee-title">Our <span className="highlight">Certifications</span></h2>
          </div>
          
          <div className="marquee-wrapper">
            <Marquee
              gradient={true}
              gradientColor={[255, 255, 255]}
              gradientWidth={50}
              speed={40}
              pauseOnHover={true}
              className="certification-marquee"
            >
              {certificationsList.map((cert) => (
                <div key={`${cert.id}-first`} className="certification-card">
                  <div className="cert-logo-container">
                    <img 
                      src={cert.logo} 
                      alt={cert.name}
                      className="cert-logo"
                    />
                  </div>
                  <div className="cert-info">
                    <h4 className="cert-name">{cert.name}</h4>
                    <span className="cert-badge">{cert.shortName}</span>
                  </div>
                </div>
              ))}
              {certificationsList.map((cert) => (
                <div key={`${cert.id}-second`} className="certification-card">
                  <div className="cert-logo-container">
                    <img 
                      src={cert.logo} 
                      alt={cert.name}
                      className="cert-logo"
                    />
                  </div>
                  <div className="cert-info">
                    <h4 className="cert-name">{cert.name}</h4>
                    <span className="cert-badge">{cert.shortName}</span>
                  </div>
                </div>
              ))}
            </Marquee>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default ProductPage;
